import { motion } from 'motion/react';
import { RiArrowRightDoubleFill, RiCheckLine, RiLoader3Line } from 'react-icons/ri';
import { Link, useParams } from 'react-router-dom';
import { StepIdEnum, useOnboardingSteps } from '../../hooks/use-onboarding-steps';
import { useTelemetry } from '../../hooks/use-telemetry';
import { buildRoute, ROUTES } from '../../utils/routes';
import { TelemetryEvent } from '../../utils/telemetry';
import { cn } from '../../utils/ui';
import { Card, CardContent } from '../primitives/card';
import { NovuLogo, PointingArrow } from './icons';
import { leftSection, logo, mainCard, stepItem, stepsList, textItem } from './progress-section.animations';

interface StepItemProps {
  step: {
    id: StepIdEnum;
    status: string;
    title: string;
    description: string;
  };
  environmentSlug?: string;
}

export function ProgressSection({ isNewHomePageEnabled }: { isNewHomePageEnabled?: boolean }) {
  const { environmentSlug } = useParams<{ environmentSlug?: string }>();
  const { steps } = useOnboardingSteps();

  return (
    <motion.div variants={mainCard} initial="hidden" animate="show" className="w-full">
      <Card
        className={cn(
          'relative flex items-stretch gap-2 rounded-xl border-neutral-100 shadow-none w-full',
          isNewHomePageEnabled ? 'flex-col bg-transparent' : 'flex-col md:flex-row'
        )}
      >
        {isNewHomePageEnabled ? <HomePageHeader /> : <WelcomeHeader />}

        <motion.div
          className={cn('flex flex-1 flex-col gap-3', isNewHomePageEnabled ? 'p-3 pt-0' : 'p-4 md:p-6')}
          variants={stepsList}
        >
          {steps.map((step, index) => (
            <StepItem key={index} step={step} environmentSlug={environmentSlug} />
          ))}
        </motion.div>

        {!isNewHomePageEnabled && (
          <motion.div variants={logo} className="absolute bottom-0 right-0 hidden md:block">
            <NovuLogo />
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
}

function HomePageHeader() {
  return (
    <motion.div variants={leftSection} className="p-3">
      <h2 className="text-label-xs text-text-strong">You're doing great work! 💪</h2>
      <p className="text-label-xs text-text-soft">Set up Notification Center to send notifications your users will love.</p>
    </motion.div>
  );
}

function StepItem({ step, environmentSlug }: StepItemProps) {
  const telemetry = useTelemetry();

  const handleStepClick = () => {
    telemetry(TelemetryEvent.WELCOME_STEP_CLICKED, {
      stepId: step.id,
      stepTitle: step.title,
      stepStatus: step.status,
    });
  };

  return (
    <motion.div className="flex w-full items-center gap-1.5 md:max-w-[370px]" variants={stepItem}>
      <div
        className={`${step.status === 'completed' ? 'bg-success' : 'shadow-xs'} flex h-6 w-6 min-w-6 items-center justify-center rounded-full`}
      >
        {step.status === 'completed' ? (
          <RiCheckLine className="h-4 w-4 text-[#ffffff]" />
        ) : (
          <RiLoader3Line className="text-foreground-400 h-4 w-4" />
        )}
      </div>

      <Link
        to={getStepRoute(step.id, environmentSlug).path}
        reloadDocument={getStepRoute(step.id, environmentSlug).isLegacy}
        className="w-full"
        onClick={handleStepClick}
      >
        <Card
          className={`shadow-xs w-full p-1 ${step.status !== 'completed' ? 'transition-all duration-200 hover:translate-x-px hover:shadow-md' : ''}`}
        >
          <CardContent className="flex flex-col rounded-[6px] bg-[#FBFBFB] px-2 py-1.5">
            <div className="flex items-center justify-between">
              <span
                className={`text-xs ${step.status === 'completed' ? 'text-foreground-400 line-through' : 'text-foreground-600'}`}
              >
                {step.title}
              </span>
              <RiArrowRightDoubleFill className="text-foreground-400 h-4 w-4" />
            </div>
            <p className="text-foreground-400 text-[10px] leading-[14px]">{step.description}</p>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}

function WelcomeHeader() {
  return (
    <motion.div
      variants={leftSection}
      className="flex w-full grow flex-col items-start justify-between gap-2 rounded-t-xl bg-[#FBFBFB] p-4 md:max-w-[380px] md:rounded-l-xl md:rounded-tr-none md:p-6"
    >
      <div className="flex w-full flex-col gap-2">
        <motion.h2 variants={textItem} className="font-label-medium text-base font-medium">
          You're doing great work! 💪
        </motion.h2>

        <div className="text-foreground-400 flex flex-col gap-4 text-sm md:gap-6">
          <motion.p variants={textItem}>Set up Notification Center to send notifications your users will love.</motion.p>
          <motion.p variants={textItem} className="hidden md:block">
            Streamline all your customer messaging in one tool and delight them at every touchpoint.
          </motion.p>
        </div>
      </div>

      <motion.div variants={textItem} className="hidden items-center gap-0 md:flex">
        <p className="text-foreground-400 text-sm">Get started with our setup guide.</p>
        <PointingArrow className="relative left-[15px] top-[-10px]" />
      </motion.div>
    </motion.div>
  );
}

function getStepRoute(stepId: StepIdEnum, environmentSlug: string = '') {
  switch (stepId) {
    case StepIdEnum.CREATE_A_WORKFLOW:
      return {
        path: buildRoute(ROUTES.WORKFLOWS, { environmentSlug }),
        isLegacy: false,
      };
    case StepIdEnum.CONNECT_EMAIL_PROVIDER:
    case StepIdEnum.CONNECT_PUSH_PROVIDER:
    case StepIdEnum.CONNECT_CHAT_PROVIDER:
    case StepIdEnum.CONNECT_SMS_PROVIDER:
      return {
        path: buildRoute(ROUTES.INTEGRATIONS, { environmentSlug }),
        isLegacy: false,
      };
    case StepIdEnum.CONNECT_IN_APP_PROVIDER:
      return {
        path: ROUTES.INBOX_EMBED,
        isLegacy: false,
      };
    case StepIdEnum.INVITE_TEAM_MEMBER:
      return {
        path: ROUTES.SETTINGS_TEAM,
        isLegacy: false,
      };
    default:
      return {
        path: '#',
        isLegacy: false,
      };
  }
}
